// app/src/components/theme/commonStyles.ts
// 공통 스타일 상수 및 유틸리티

import { Button, Theme } from "@mui/material"
import { styled } from "@mui/material/styles"
import { colors, brandColors } from "./commonStylesColor"

/**
 * 공통 Border Radius 값
 */
export const borderRadius = {
  none: 0,
  xs: "4px",
  sm: "6px",
  md: "8px",
  lg: "10px",
  xl: "12px",
  full: "999px",
} as const

/**
 * 공통 Spacing 값 (px 단위)
 */
export const spacing = {
  xxs: 1.6,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 26.4,
  xxl: 32,
} as const

export const panelSectionHeader = {
  iconSize: 21,
  gap: spacing.xs,
  leftInset: spacing.md,
  topInset: spacing.md,
} as const

/**
 * 공통 Font Size 값
 */
export const fontSize = {
  xs: 11,
  sm: 12,
  md: 13,
  base: 14,
  panelTitle: 16,
  lg: 15,
  xl: 18,
  xxl: "1.2rem",
  icon: "30px",
} as const

export const getPanelHeaderIconStyles = (theme: Theme) => ({
  fontSize: fontSize.xl,
  color: theme.palette.action.active,
  flexShrink: 0,
})

/**
 * 공통 Font Weight 값
 */
export const fontWeight = {
  light: 400,
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: "bold",
} as const

export const ImageViewerActionButton = styled(Button)({
  height: 36,
  fontSize: fontSize.base,
  fontWeight: fontWeight.medium,
  borderRadius: borderRadius.xs,
  textTransform: "none",
  flexShrink: 0,
  whiteSpace: "nowrap",
})

/**
 * 공통 Opacity 값
 */
export const opacity = {
  disabled: 0.7,
  hover: 0.8,
  active: 0.9,
  full: 1,
} as const

/**
 * 공통 Background Color (고정 색상)
 */
export const backgroundColor = {
  primary: brandColors.accent.pink,
  secondary: brandColors.accent.blue,
  transparent: "transparent",
  error: "rgba(255, 0, 0, 0.03)",
} as const

/**
 * 공통 Box Shadow / Filter 효과
 */
export const shadows = {
  // Drop Shadow 효과
  dropShadow: {
    sm: "drop-shadow(0 1px 1px rgba(0, 0, 0, 0.1))",
    md: "drop-shadow(0 2px 2px rgba(0, 0, 0, 0.2))",
    icon: "drop-shadow(0 2px 2px rgba(0, 0, 0, 0.2))",
    divider: "drop-shadow(0 1px 2px rgba(255,255,255,0.3))",
  },
  // Box Shadow 효과
  boxShadow: {
    inset: "inset 0 0 3px rgba(0,0,0,0.1)",
    insetHover: "inset 0 0 5px rgba(0,0,0,0.15)",
  },
} as const

/**
 * 공통 Transition 효과
 */
export const transitions = {
  default: "all 0.2s ease-in-out",
  width: "width 0.3s ease",
  fast: "0.15s ease",
  medium: "0.3s ease",
} as const

/**
 * 공통 Z-Index 레이어
 */
export const zIndex = {
  base: 1,
  overlay: 1000,
  modal: 1300,
  tooltip: 1500,
} as const

/**
 * 공통 스크롤바 스타일 (theme-aware)
 */
export const scrollbarStyles = (theme: Theme) => ({
  scrollbarColor: "rgba(255,255,255,0.3) transparent",
  "&::-webkit-scrollbar": {
    width: "6px",
    height: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
    margin: "0px",
    border: "none",
    boxShadow: "none",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(255,255,255,0.25)",
    backdropFilter: "blur(4px)",
    borderRadius: borderRadius.sm,
    backgroundClip: "padding-box",
    boxShadow: shadows.boxShadow.inset,
    transition: transitions.default,
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "rgba(255,255,255,0.4)",
    boxShadow: shadows.boxShadow.insetHover,
  },
})

/**
 * 공통 Hover 효과 (theme-aware)
 */
export const hoverStyles = (theme: Theme) => ({
  backgroundColor: theme.custom.hoverBg,
  opacity: opacity.hover,
})

/**
 * 공통 Border 스타일 (theme-aware)
 */
export const borderStyles = (theme: Theme) => ({
  solid: `1px solid ${theme.custom.borderColor}`,
  divider: `1px solid ${colors.gray[200]}`,
})

/**
 * 공통 버튼 스타일 (theme-aware)
 */
export const buttonStyles = {
  base: (theme: Theme) => ({
    borderRadius: borderRadius.lg,
    fontSize: fontSize.base,
    filter: shadows.dropShadow.md,
    "&:hover": {
      opacity: opacity.active,
    },
  }),
  primary: (theme: Theme) => ({
    ...buttonStyles.base(theme),
    backgroundColor: backgroundColor.primary,
    color: theme.custom.mainFontColor,
  }),
  secondary: (theme: Theme) => ({
    ...buttonStyles.base(theme),
    backgroundColor: backgroundColor.secondary,
    color: theme.custom.mainFontColor,
  }),
}

/**
 * 공통 텍스트 스타일 (theme-aware)
 */
export const textStyles = {
  title: (theme: Theme) => ({
    fontWeight: fontWeight.bold,
    color: theme.custom.informationTitleFontColor,
    fontSize: fontSize.xxl,
    filter: shadows.dropShadow.sm,
  }),
  body: (theme: Theme) => ({
    fontSize: fontSize.base,
    color: theme.custom.mainFontColor,
  }),
  subtitle: (theme: Theme) => ({
    fontSize: fontSize.base,
    fontWeight: fontWeight.light,
    color: theme.custom.mainFontColor,
  }),
  caption: (theme: Theme) => ({
    fontSize: fontSize.sm,
    color: theme.custom.subFontColor,
  }),
}

/**
 * 공통 Glass 카드 오버레이 스타일 (theme-aware)
 */
export const overlayStyles = (theme: Theme) => ({
  position: "absolute" as const,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  border: `1px solid ${theme.custom.isDarkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(255, 255, 255, 0.8)"}`,
  boxShadow: `
    inset 0 1px 0 ${theme.custom.isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(255, 255, 255, 0.8)"},
    0 4px 30px rgba(0, 0, 0, 0.1),
    0 1px 8px rgba(0, 0, 0, 0.05)
  `,
  display: "flex",
  flexDirection: "column" as const,
  justifyContent: "center",
  alignItems: "center",
  zIndex: zIndex.overlay,
  borderRadius: borderRadius.xl,
})

/**
 * 공통 아이콘 스타일 (theme-aware)
 */
export const iconStyles = (theme: Theme) => ({
  fontSize: fontSize.icon,
  color: theme.custom.dashboardTitleFontColor || theme.custom.informationTitleFontColor,
  filter: shadows.dropShadow.icon,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
})

/**
 * 공통 구분선 스타일 (theme-aware)
 */
export const dividerStyles = (theme: Theme) => ({
  vertical: {
    color: theme.custom.subFontColor,
    margin: "0px 3px",
  },
  horizontal: {
    width: "85%",
    bgcolor: "rgba(128, 128, 128, 0.1)",
    filter: shadows.dropShadow.divider,
  },
})

/**
 * 공통 Padding 패턴
 */
export const padding = {
  xs: "4px",
  sm: "8px",
  md: "12px 16px",
  lg: "16px",
  paper: "12px 12px 16px 16px", // PatientInfoPaper의 특수 패딩
  paper2: "8px 8px 12px 12px", // PatientInfoPaper의 특수 패딩
  sms: "1.6px 4px", // SMS 버튼 특수 패딩
} as const

/**
 * 공통 Margin 패턴
 */
export const margin = {
  xxs: "4px",
  xs: "8px",
  sm: "12px",
  md: "16px",
  lg: "26.4px",
} as const

/**
 * 공통 Flex 레이아웃 패턴
 */
export const flexLayouts = {
  // 기본 Flex 컨테이너
  flex: {
    display: "flex",
  } as const,

  // Flex Column
  flexColumn: {
    display: "flex",
    flexDirection: "column" as const,
  } as const,

  // Flex Row (기본값이지만 명시적으로)
  flexRow: {
    display: "flex",
    flexDirection: "row" as const,
  } as const,

  // Center 정렬
  center: {
    display: "flex",
    alignItems: "center" as const,
    justifyContent: "center" as const,
  } as const,

  // 수직 가운데 정렬
  centerVertical: {
    display: "flex",
    alignItems: "center" as const,
  } as const,

  // 수평 가운데 정렬
  centerHorizontal: {
    display: "flex",
    justifyContent: "center" as const,
  } as const,

  // Space Between
  spaceBetween: {
    display: "flex",
    justifyContent: "space-between" as const,
  } as const,

  // Space Between + Center Vertical
  spaceBetweenCenter: {
    display: "flex",
    justifyContent: "space-between" as const,
    alignItems: "center" as const,
  } as const,

  // Flex End 정렬
  flexEnd: {
    display: "flex",
    alignItems: "flex-end" as const,
  } as const,

  // Flex Start 정렬
  flexStart: {
    display: "flex",
    alignItems: "flex-start" as const,
  } as const,
} as const

/**
 * 공통 Position 패턴
 */
export const positions = {
  relative: {
    position: "relative" as const,
  } as const,

  absolute: {
    position: "absolute" as const,
  } as const,

  absoluteFull: {
    position: "absolute" as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  } as const,
} as const

/**
 * 공통 Text 정렬 패턴
 */
export const textAlign = {
  left: "left" as const,
  center: "center" as const,
  right: "right" as const,
} as const

/**
 * 공통 Overflow 패턴
 */
export const overflow = {
  hidden: {
    overflow: "hidden" as const,
  } as const,

  auto: {
    overflow: "auto" as const,
  } as const,

  scroll: {
    overflow: "scroll" as const,
  } as const,

  hiddenX: {
    overflowX: "hidden" as const,
  } as const,

  hiddenY: {
    overflowY: "hidden" as const,
  } as const,

  autoY: {
    overflowY: "auto" as const,
  } as const,
} as const
